import { Component, inject, signal, OnInit } from '@angular/core';

import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { Load } from '../../../../core/models/load.model';
import { Stop } from '../../../../core/models/stop.model';
import { StopType } from '../../../../core/enums/stop-type.enum';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { GoogleMapsService } from '../../../../core/services/google-maps.service';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { AppType, KeyValue } from '../../../../core/models/app-types.model';
import { KeyValueArrayInputComponent } from '../../../../shared/ui/forms/key-value-array-input/key-value-array-input.component';
import { NumberInputComponent } from '../../../../shared/ui/forms/number-input/number-input.component';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import { ReferenceOptions } from '../../../../core/enums/reference.enum';
import { ItemOptions } from '../../../../core/enums/item.enum';
import { LoadService } from '../../../../core/services/load.service';
import { SegmentService } from '../../../../core/services/segment.service';
import { Address } from '../../../../core/models/address.model';
import { ContactInfo } from '../../../../core/models/contact-info.model';
import { TextareaComponent } from '../../../../shared/ui/forms/textarea/textarea.component';
import { AddressFormComponent } from '../../../../shared/ui/forms/address-form/address-form.component';
import { ContactInputComponent } from '../../../../shared/ui/forms/contact-input/contact-input.component';
import { AppointmentControlComponent } from '../../../../shared/ui/forms/appointment-control/appointment-control.component';
import {
  SelectOption,
  SelectComponent,
} from '../../../../shared/ui/forms/select/select.component';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';

@Component({
  selector: 'app-edit-stop-form',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    KeyValueArrayInputComponent,
    NumberInputComponent,
    AddressFormComponent,
    InputComponent,
    TextareaComponent,
    ContactInputComponent,
    AppointmentControlComponent,
    SelectComponent
],
  standalone: true,
  templateUrl: './edit-stop-form.component.html',
})
export class EditStopFormComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly segmentService = inject(SegmentService);
  private readonly toastService = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly loadingService = inject(LoadingService);
  private readonly googleMapsService = inject(GoogleMapsService);

  referenceTypes: AppType[] = ReferenceOptions.map((t) => ({
    id: t.value,
    label: t.label,
  }));
  itemTypes: AppType[] = ItemOptions.map((t) => ({
    id: t.value,
    label: t.label,
  }));
  stopTypeOptions: SelectOption[] = [
    { value: 'pickup', label: 'Pickup' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'both', label: 'Pickup & Delivery (Both)' },
    { value: 'handoff', label: 'Relay Handoff' },
    { value: 'yard_drop', label: 'Yard Drop' },
    { value: 'yard_hook', label: 'Yard Hook' },
    { value: 'custom', label: 'Custom Waypoint' },
  ];

  protected readonly load = this.modalService.state()!.data.load;
  protected readonly stopNumber: number =
    this.modalService.state()!.data.stopNumber;
  protected readonly onSuccess = this.modalService.state()!.data.onSuccess;
  protected readonly isSaving = signal(false);
  protected form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      stopType: ['pickup' as StopType, [Validators.required]],
      companyName: [null],
      address: [null as Address | null, [Validators.required]],
      contactInfo: [null as ContactInfo | null],
      operatingHours: [''],
      appointment: [null as any],
      instructions: [''],
      notes: [''],
      weight: [null as number | null],
      weightTicket: [null as number | null],
      sealNumber: [null as string | null],
      reeferTemp: [null as number | null],
      referenceNumbers: [[] as KeyValue[]],
      itemCounts: [[] as KeyValue[]],
      milesToNext: [null],
    });

    // Initialize form with data from the load
    this.initializeForm();
  }

  private initializeForm(): void {
    const stop = this.load.stops.find(
      (s: Stop) => s.stopNumber === this.stopNumber
    );
    this.form.patchValue(
      {
        stopType: stop.stopType || 'pickup',
        companyName: stop.companyName || null,
        address: stop.address || null,
        contactInfo: stop.contactInfo || null,
        operatingHours: stop.operatingHours || '',
        appointment: stop.appointment || null,
        instructions: stop.instructions || '',
        notes: stop.notes || '',
        weight: stop.weight || null,
        weightTicket: stop.weightTicket || null,
        sealNumber: stop.sealNumber || null,
        reeferTemp: stop.reeferTemp || null,
        referenceNumbers: stop.referenceNumbers || [],
        itemCounts: stop.itemCounts || [],
        milesToNext: stop.milesToNext || null,
        // ... patch other values from stop
      },
      { emitEvent: false } // Prevent infinite loops
    );
  }

  ngOnInit(): void {
    // Ensure at least one row exists for a new form
    if (this.referenceNumbers.length === 0) {
      this.addReferenceNumber();
    }
    if (this.itemCounts.length === 0) {
      this.addItemCount();
    }
  }

  // --- FormArray Getters ---
  get referenceNumbers(): FormArray {
    return this.form.get('referenceNumbers') as FormArray;
  }

  get itemCounts(): FormArray {
    return this.form.get('itemCounts') as FormArray;
  }

  // --- Reference Number Methods ---
  addReferenceNumber(value: KeyValue | null = null): void {
    this.referenceNumbers.push(this.fb.control(value));
  }

  removeReferenceNumber(index: number): void {
    this.referenceNumbers.removeAt(index);
  }

  // --- Item Count Methods ---
  addItemCount(value: KeyValue | null = null): void {
    this.itemCounts.push(this.fb.control(value));
  }

  removeItemCount(index: number): void {
    this.itemCounts.removeAt(index);
  }

  cancel(): void {
    this.modalService.close();
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Set local saving state
    this.isSaving.set(true);

    // Show global loading overlay
    this.loadingService.show({
      variant: 'overlay',
      title: 'Updating Stop Details',
      subtitle: 'Saving your changes...',
    });

    try {
      const cleanseAppointment = (appointment: any): any => {
        if (!appointment) return null;
        const cleanseDateTime = (dt: any) =>
          dt && typeof dt === 'object'
            ? {
                date: typeof dt.date === 'string' ? dt.date : '',
                time: typeof dt.time === 'string' ? dt.time : '',
              }
            : null;
        return {
          ...appointment,
          from: cleanseDateTime(appointment.from),
          to: cleanseDateTime(appointment.to),
        };
      };

      const stops = this.load.stops.map((s: Stop) => {
        if (s.stopNumber === this.stopNumber) {
          const raw = this.form.getRawValue();
          return {
            ...s,
            ...raw,
            appointment: cleanseAppointment(raw.appointment),
          };
        }
        return s;
      });
      
      // Ensure the newly updated stop has valid coordinates before saving
      await this.googleMapsService.ensureStopsGeocoded(stops);
      
      const totalMiles = stops.reduce(
        (acc: number, s: Stop) => acc + (s.milesToNext || 0),
        0
      );
      const updatedData: Partial<Load> = { stops, loadedMiles: totalMiles };
      
      this.loadService.updateLoad(this.load.id, updatedData).subscribe({
        next: () => {
          void this.segmentService.triggerRouteRecalculation(this.load);
          this.loadingService.hide();
          this.modalService.close();
          this.onSuccess?.();
          setTimeout(() => {
            this.toastService.show('success', 'Stop updated successfully', {
              title: 'Success',
              duration: 4000,
            });
          }, 100);
        },
        error: (_error) => {
          this.loadingService.hide();
          this.toastService.show(
            'error',
            'Failed to update stop. Please try again.',
            {
              title: 'Update Failed',
              showClose: true,
              duration: 0,
            }
          );
        },
        complete: () => {
          this.isSaving.set(false);
        }
      });
    } catch {
      this.loadingService.hide();
      this.toastService.show(
        'error',
        'An unexpected error occurred.',
        {
          title: 'Update Failed',
          showClose: true,
          duration: 0,
        }
      );
      this.isSaving.set(false);
    }
  }
}
